import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Table, TableHeaderCheckboxToggleEvent, TableModule, TableRowSelectEvent } from 'primeng/table';
import { Subscription } from 'rxjs';
import { PaginationService } from '../../shared_services/pagination.service';
import { MultiSelect } from 'primeng/multiselect';
import { TableConfig } from '../../../../interfaces/table-config';
import { CustomDatePipe } from '../../../../pipes/custom-date.pipe';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ImportsModule } from '../../../imports/imports';
import { CommonModule } from '@angular/common';
import { TablerIconComponent } from 'angular-tabler-icons';

@Component({
  selector: 'app-primeng-table',
  templateUrl: './primeng-table.component.html',
  styleUrl: './primeng-table.component.scss',
  providers: [CustomDatePipe],
  imports : [
    TableModule,
    ImportsModule,
    CommonModule,
    CustomDatePipe,
    TablerIconComponent,
  ]
})
export class PrimengTableComponent implements OnChanges, AfterViewInit, OnDestroy {

  @Input() tableData: any[] = [];
  @Input() columns: any[] = [];
  @Input() tableStyle: { [key: string]: string } = {};
  @Input() paginator: boolean = false;
  @Input() rows: number = 10;
  @Input() responsiveLayout: string = 'scroll';
  @Input() styleClass: string = '';
  @Input() sortField: string = '';
  @Input() sortOrder: number = 1;
  @Input() customTemplates: any;
  @Input() tableId: any;
  pageLinks: number = 3;
  @Input() metricCard: any;
  @Input() totalRecords: any;
  @Input() tableConfig?: TableConfig;
  selectedRow: any[] = [];
  @Output() selectedCheckedRow = new EventEmitter<any>();
  @Output() pageNumber = new EventEmitter<Object>();
  @Output() tableReference = new EventEmitter<Table>();
  @ViewChild('dt') dt!: Table; // Reference to p-table
  @ViewChild('multiSelect') multiSelect!: MultiSelect;
  @Input() isFilterRequired: boolean = false;
  @Input() currentPageNumber!:number;
  globalFilterFields: string[] = [];
  tableDataObj: any = {};
  first = 0;
  currentFirst: number = 10;
  public pageIndexSubscription!: Subscription;
  public numberOfRowsInPageSubsciption!: Subscription
  filters: { [key: string]: any[] } = {};
  filteredTableData: any[] =[];
  numRows!:number
  @Input() minimumEntry!:number;

  constructor(private paginationService: PaginationService, private router: Router, private customDatePipe: CustomDatePipe) { }



  ngOnInit() {
    this.resetFilter()
    this.transformTableData();
    this.currentPageAndRouteSubscription()
    this.numRows = this.rows
  }


  currentPageAndRouteSubscription() {
    this.paginationService.initializeTable(this.tableId, this.rows);
    this.numberOfRowsInPageSubsciption = this.paginationService.getNumberOfRowsInPage$(this.tableId).subscribe(
      (numberOfRows: number) => {
        this.rows = numberOfRows;
      });

    this.pageIndexSubscription = this.paginationService.getPageIndex$(this.tableId).subscribe(
      (currentPage: number) => {
        let viewFirstElement = currentPage * this.rows;
        if (viewFirstElement >= this.totalRecords && this.totalRecords > 10) {
          viewFirstElement = (currentPage - 1) * this.rows;
        }
        this.first = viewFirstElement;
        this.applyFilters();
      }
    );

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.paginationService.setPageIndex(this.tableId, 0);
        this.paginationService.setNumberOfRowsInPage(this.tableId, 10);
      }
    });
  }

  ngAfterViewInit() {
    this.tableReference.emit(this.dt); // Emit the reference
    this.hideDefaultPagenator();
    setTimeout(() => {
      const nativeTable = this.dt.el.nativeElement.querySelector('table') as HTMLTableElement;
      if (nativeTable) {
        nativeTable.id = this.tableId; // Assigning the ID dynamically
      }
    });
  }

  ngOnDestroy() {
    if (this.pageIndexSubscription) {
      this.pageIndexSubscription.unsubscribe();
    }
    if (this.numberOfRowsInPageSubsciption) {
      this.pageIndexSubscription.unsubscribe();
    }
    this.resetFilter()
  }

  resetFilter() {
    this.filters = {};
    this.columns?.forEach(col => {
      this.filters[col.key] = [];
      col.value = [];
    });
    if(this.tableId === 'ticket-dashboard') {
      this.globalFilterFields = this.columns?.map(col => col.key);
    } else {
      this.globalFilterFields = this.columns?.filter(col => col.hasMultiFilter).map(col => col.key);
    }
  }

  hideDefaultPagenator() {
    const paginatorElement = this.dt.el.nativeElement.querySelector('.p-paginator');
    if (paginatorElement) {
      if (this.tableId === 'ticket-dashboard' || this.tableId === 'utterances') {
        paginatorElement.style.display = 'none';
      }
    }
  }

  getColumnHeaderStyle(col: any): { [key: string]: string } {
    if (col?.hasCheckbox) {
      return { width: '40px' };
    }
    if (col?.isMultilineHeader && col?.styles?.header) {
      return col.styles.header;
    }
    return {};
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tableData'] || changes['columns']) {
      this.transformTableData();
      this.applyFilters();
    }
    if(this.currentPageNumber){
      this.first = (this.currentPageNumber-1) * this.rows;
      this.paginationService.setPageIndex(this.tableId,Math.floor(this.first / this.rows))
      this.paginationService.setNumberOfRowsInPage(this.tableId,this.rows);
    }
  }

  transformTableData() {
    let dateTypeColumnKeys = this.columns?.filter((element) => element.isDateTypeColumn).map((column) => column.key);
    const dateFormat =this.tableId == 'ticket-dashboard' ? 'MM/DD/YYYY HH:MM'  : this.tableId == 'wiseflow_list' ? 'MM-DD-YYYY' : 'MM/DD/YYYY';
    const transform = (data: any, tableDataObj: any) => {
      Object.keys(data)?.forEach(key => {
        const value = data[key];
        if (value instanceof FormControl || value instanceof FormGroup || value instanceof FormArray) {
          return;
        }
        if (value instanceof File || value instanceof Blob) {
          this.addToArray(tableDataObj, key, value)
        } else if (typeof value === 'string' || typeof value === 'number') {
          let updatedValue = dateTypeColumnKeys.includes(key) ? this.customDatePipe.transform(value, dateFormat) : value;
          this.addToArray(tableDataObj, key, updatedValue)
        } else if (typeof value === 'object' && value !== null) {
          if (!tableDataObj[key]) {
            tableDataObj[key] = {};
          }
          transform(value, tableDataObj[key]);
        } else if (value === null) {
          if (!Array.isArray(tableDataObj[key])) {
            tableDataObj[key] = [];
          }
          if (!tableDataObj[key].includes('-')) {
            tableDataObj[key].push('-');
          }
        }
      });
    };

    this.tableDataObj = {}; // Initialize tableDataObj
    this.tableData?.forEach(data => {
      transform(data, this.tableDataObj);
      for (let dateKey of dateTypeColumnKeys) {
        data[dateKey] = this.customDatePipe.transform(data[dateKey],dateFormat);
      }
    });
    this.filteredTableData = this.tableData;
    if (this.isFilterRequired) {
      this.filterTableObject();
    }
  }

  filterTableObject() {
    let formattedTableObj: any = {};
    Object.keys(this.tableDataObj).forEach((key: any) => {
      let value = this.tableDataObj[key];
      if (Array.isArray(value)) {
        formattedTableObj[key] = value;
      } else {
        this.processNestedObject(value, formattedTableObj);
      }
    });
    this.tableDataObj = formattedTableObj;
  }

  private processNestedObject(value: any, formattedTableObj: any) {
    Object.keys(value).forEach((nestedKey: any) => {
      let nestedValue = value[nestedKey];
      Object.keys(nestedValue).forEach((nestedKey2: any) => {
        let currentValue = nestedValue[nestedKey2];
        this.addUniqueItems(currentValue, nestedKey2, formattedTableObj);
      });
    });
  }

  private addUniqueItems(currentValue: any, nestedKey2: string, formattedTableObj: any) {
    if (!formattedTableObj[nestedKey2]) {
      formattedTableObj[nestedKey2] = [];
    }
    if (Array.isArray(currentValue)) {
      currentValue.forEach((item: any) => {
        if (!formattedTableObj[nestedKey2].includes(item)) {
          formattedTableObj[nestedKey2].push(item);
        }
      });
    } else if (!formattedTableObj[nestedKey2].includes(currentValue)) {
      formattedTableObj[nestedKey2].push(currentValue);
    }
  }

  addToArray(obj: any, key: string, value: any): void {
    obj[key] = Array.isArray(obj[key]) ? obj[key] : [];
    
    if (!obj[key].includes(value)) {
        obj[key].push(value);
    }
  }

  getSeverity(status: string): "danger" | "success" | "info" | "warning" | null | undefined | 'secondary' {
    if (!status) {
      return null;
    }
    switch (status.toLowerCase()) {
      // case 'unqualified':
      //     return 'danger';
      case 'ai assisted':
        return 'info';
      case 'ai responded':
        return 'success';
      case 'manually handled':
        return 'warning';
      // case 'need assisstance':
      //     return 'secondary';
      default:
        return 'danger'; // or any other default value appropriate for your application
    }
  }

  getNestedBodyValue(obj: any, path: string): any {
    return path?.split('.')?.reduce((acc, part) => acc?.[part], obj) ?? '-';
  }

  getNestedValue(obj: any, path: string): any[] {
    const value = path.split('.').reduce((acc, part) => acc?.[part], obj);
    const arrayValue = Array.isArray(value) ? value : [];
    return arrayValue;
  }


  next() {
    this.first = this.first + this.rows;
  }

  prev() {
    this.first = this.first - this.rows;
  }

  reset() {
    this.first = 0;
  }

  pageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
    this.paginationService.setPageIndex(this.tableId,Math.floor(this.first / this.rows))
    this.paginationService.setNumberOfRowsInPage(this.tableId,this.rows);
    this.pageNumber.emit({ pageNumber: Math.floor(this.first / this.rows) + 1, totalEntryPerPage: this.rows });
    this.applyFilters();
  }

  openDropdown(multiSelect: MultiSelect) {
    multiSelect.show()
    if (multiSelect) {
      const toggleButton = multiSelect.el.nativeElement.querySelector('.p-multiselect-trigger');
      if (toggleButton) {
        toggleButton.click();
      } else {
        console.error("Toggle button not found");
      }
    } else {
      console.error("MultiSelect component not found");
    }
  }

  filterTable(key: string, value: any[]) {
    this.filters[key] = value;
    this.applyFilters();
  }

  applyFilters() {
    if (!Array.isArray(this.tableData)) {
      console.error('Data is not an array or is undefined:', this.tableData);
      return;
    }

    this.filteredTableData = this.tableData.filter(row => this.filterRow(row));
  }

  filterRow(row: any) {
    return Object.keys(this.filters).every(key => {
      const filterValues = this.filters[key] || [];
      if (filterValues.length === 0) {
        return true;
      }

      const values = this.findValues(row, key);
      if (values.length === 0) {
        console.log(`No match for ${key} in row:`, row);
        return false;
      }

      return this.matchValues(values, filterValues);
    });
  }

  findValues(row: any, key: string) {
    const results:any = [];
    let initialValue = this.getValue(row, key);
    if (initialValue !== undefined) {
      results.push(initialValue);
      return results
    }
    this.collectNestedValues(row, key, results);
    return results;
  }

  getValue(row: any, key: string) {
    const keys = key.split('.');
    if (row.hasOwnProperty(keys[0])) {  
      return keys.reduce((acc: any, curr: string) => acc?.[curr], row);
    }
    return undefined;
  }

  collectNestedValues(row: any, key: string, results: any[]) {
    for (const prop in row) {
      const value = row[prop];
      if (Array.isArray(value)) {
        this.collectFromArray(value, key, results);
      } else if (this.isObject(value)) {
        this.collectFromObject(value, key, results);
      }
    }
  }

  collectFromArray(array: any[], key: string, results: any[]) {
    array.forEach(item => {
      if (this.isObject(item)) {
        const nestedResults = this.findValues(item, key);
        results.push(...nestedResults);
      }
    });
  }

  collectFromObject(obj: any, key: string, results: any[]) {
    const nestedResults = this.findValues(obj, key);
    results.push(...nestedResults);
  }

  isObject(value: any) {
    return typeof value === 'object' && value !== null;
  }

  matchValues(values: any[], filterValues: any[]) {
    return values.some(value => filterValues.includes(value) || (filterValues.includes('-') && value === null));
  }


  selectAll(key: string, multiSelect: any) {
    const filteredOptions = multiSelect.visibleOptions() || this.getNestedValue(this.tableDataObj, key);
    this.filters[key] = filteredOptions;
    const column = this.columns.find(col => col.key === key);
    if (column) {
      column.value = structuredClone(filteredOptions);
    }
    this.filterTable(key, filteredOptions);
  }

  clearAll(key: string) {
    this.filters[key] = [];
    const column = this.columns.find(col => col.key === key);
    if (column) {
      column.value = [];
    }
    this.filterTable(key, []);
  }

  headerCheckboxChange(event: TableHeaderCheckboxToggleEvent) {
    this.selectedCheckedRow.emit(this.selectedRow);
  }

  rowSelectUnselectChange(event: TableRowSelectEvent<any>) {
    this.selectedCheckedRow.emit(this.selectedRow);
  }
}

